import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';

import {
  Bars3Icon,
  BellIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

function SparesHeader({ toggleSidebar }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingDeposits, setPendingDeposits] = useState(0);

  useEffect(() => {
    const depositsQuery = query(
      collection(db, 'cashDeposits'),
      where('status', '==', 'submitted')
    );

    const unsubscribe = onSnapshot(depositsQuery, (snapshot) => {
      setPendingDeposits(snapshot.size);
    });

    return () => unsubscribe();
  }, []);

  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
          title="Back to Main Admin"
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>

        {/* Sidebar Toggle */}
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search..."
            className="w-64 pl-10 pr-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors relative"
          >
            <BellIcon className="w-6 h-6" />
            {pendingDeposits > 0 && (
              <span className="absolute top-0 right-0 w-5 h-5 bg-red-600 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {pendingDeposits}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-gray-900 font-semibold">Notifications</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {pendingDeposits > 0 ? (
                  <div className="p-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <BellIcon className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-gray-900 text-sm font-medium">
                          {pendingDeposits} Cash Deposit{pendingDeposits > 1 ? 's' : ''} Pending
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          Porters are waiting for verification
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-gray-500">No new notifications</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
          <div className="text-right">
            <p className="text-gray-900 text-sm font-medium">Spares Admin</p>
            <p className="text-gray-500 text-xs">
              {currentUser?.email || 'admin@zonex.com'}
            </p>
          </div>
          <UserCircleIcon className="w-10 h-10 text-gray-500" />
        </div>
      </div>
    </header>
  );
}

export default SparesHeader;